import {
    Box,
    Collapse,
    FormHelperText,
    Grid,
    InputLabel,
    Paper,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { Control, useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { TransitionGroup } from 'react-transition-group';
import {
    useCalculatePriceMutation,
    useGetIndividualOwnerAssetsQuery,
} from '../../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../../shared/AuthRoles';
import {
    camelCaseToSentenceCase,
    formatCurrency,
    sum,
} from '../../../../../shared/utils';
import { CleaningFeeType, ExperienceType } from '../../../../enums/experience';
import FormAutocomplete from '../../../../shared/FormAutocomplete';
import FormCalendar from '../../../../shared/FormCalendar';
import FormCardSelect from '../../../../shared/ReservationSpacesExperienceForm';
import FormCounter from '../../../../shared/FormCounter';
import {
    Experience,
    FormValues,
    PropertyDetails,
} from '../../../../types/experience';
import { getAvailableSpaces } from '../../../Reservations/ReservationRequest/Shared/reservationUtils';
import { ExperiencePricingSchema } from '../../Form/Pricing/schema';

const css = `
    .rdp-month_caption {
        display: none;
    }

    /* Scale down further for screens 768px or less */
    @media (max-width: 768px) {
        .rdp-root {
            --rdp-day_button-width: 32px !important;
            --rdp-day_button-height: 32px !important;
            --rdp-day-width: 32px !important;
            --rdp-day-height: 32px !important;
        }
    }

    /* Scale down even further for screens 480px or less */
    @media (max-width: 480px) {
        .rdp-root {
            --rdp-day_button-width: 28px !important;
            --rdp-day_button-height: 28px !important;
            --rdp-day-width: 28px !important;
            --rdp-day-height: 28px !important;
        }
    }
`;

// Cache last-known labels to prevent flicker during deselection animations
const zoneLabelCache: Record<number, string> = {};

const getZoneLabel = (zoneId: number, availableZones: any[]) => {
    const zones = Array.isArray(availableZones) ? availableZones : [];
    const zone = zones.find((z) => z.id === zoneId);
    const label =
        zone?.assignedName || camelCaseToSentenceCase(zone?.name || '');
    if (label) {
        zoneLabelCache[zoneId] = label;
        return label;
    }
    return zoneLabelCache[zoneId] || '';
};

export const getCleaningFeeText = (charge: any, availableZones: any[]) => {
    const tieredMultiplier = charge.feeMultipliers.find(
        (multiplier: any) => multiplier.type === 'roomTier',
    );

    return charge.type === 'fixed'
        ? { description: 'Minimum Fee', amount: formatCurrency(charge.total) }
        : charge.type === 'perNight'
          ? {
                description: `Minimum Fee: ${charge.feeMultipliers
                    .map(
                        (multiplier: any) =>
                            `${multiplier.value} ${multiplier.type}${
                                multiplier.value > 1 ? 's' : ''
                            }`,
                    )
                    .join(' x ')} x ${charge.unitPrice}`,
                amount: formatCurrency(charge.total),
            }
          : ['perRoom', 'perRoomPerNight'].includes(charge.type)
            ? {
                  description: `${charge.feeMultipliers
                      .map(
                          (multiplier: any) =>
                              `${multiplier.value} ${multiplier.type}${
                                  multiplier.value > 1 ? 's' : ''
                              }`,
                      )
                      .join(' x ')} x ${charge.unitPrice}`,
                  amount: formatCurrency(charge.total),
              }
            : charge.type === 'perTier'
              ? {
                    description: `Applied Tier Fee (${tieredMultiplier.roomTier.from}-${tieredMultiplier.roomTier.to} rooms)`,
                    amount: formatCurrency(charge.total),
                }
              : charge.type === 'perTierPerNight'
                ? {
                      description: `Applied Tier Fee: ${charge.feeMultipliers
                          .filter(
                              (multiplier: any) =>
                                  multiplier.type !== 'roomTier',
                          )
                          .map(
                              (multiplier: any) =>
                                  `${multiplier.value} ${multiplier.type}${
                                      multiplier.value > 1 ? 's' : ''
                                  }`,
                          )
                          .join(' x ')} x ${charge.unitPrice} (${
                          tieredMultiplier.roomTier.from
                      }-${tieredMultiplier.roomTier.to} rooms) `,
                      amount: formatCurrency(charge.total),
                  }
                : charge.type === 'perZone'
                  ? (() => {
                        const zoneId = charge?.feeMultipliers?.[0]?.zoneId;
                        const label = getZoneLabel(zoneId, availableZones);
                        return label
                            ? {
                                  description: label,
                                  amount: formatCurrency(charge.total),
                              }
                            : {
                                  description: '',
                                  amount: formatCurrency(charge.total),
                              };
                    })()
                  : charge.type === 'perZonePerNight'
                    ? (() => {
                          const zoneId = charge?.feeMultipliers?.[0]?.zoneId;
                          const label = getZoneLabel(zoneId, availableZones);
                          const multipliersText = (charge?.feeMultipliers || [])
                              .filter(
                                  (multiplier: any) =>
                                      multiplier.type !== 'zone',
                              )
                              .map(
                                  (multiplier: any) =>
                                      `${multiplier.value} ${multiplier.type}${
                                          multiplier.value > 1 ? 's' : ''
                                      }`,
                              )
                              .join(' x ');
                          return label
                              ? {
                                    description: ` ${label}: ${multipliersText} x ${charge.unitPrice}`,
                                    amount: formatCurrency(charge.total),
                                }
                              : {
                                    description: `${multipliersText} x ${charge.unitPrice}`,
                                    amount: formatCurrency(charge.total),
                                };
                      })()
                    : { description: ` `, amount: formatCurrency(0) };
};

const CalendarView = ({
    experience,
    availableZones,
    parentControl,
}: {
    experience: Experience;
    availableZones: any[];
    parentControl: Control<z.infer<typeof ExperiencePricingSchema>>;
}) => {
    const { control, setValue } = useFormContext();
    const loggedInUser = UserRole();
    const { data: allAssets } = useGetIndividualOwnerAssetsQuery({
        url: `owners/${loggedInUser?.company}/modules`,
        status: 'published',
    });

    const [calculatePrice] = useCalculatePriceMutation();

    const values = useWatch({ control });

    const date = useWatch({ control, name: '_demo.date' });
    const minHireHours = useWatch({
        control: parentControl,
        name: 'minHireHours',
    });
    const bedrooms = useWatch({
        control: parentControl,
        name: 'details.bedrooms',
    });
    const privateOffices = useWatch({
        control: parentControl,
        name: 'details.privateOffices',
    });
    const meetingRooms = useWatch({
        control: parentControl,
        name: 'details.meetingRooms',
    });
    const desks = useWatch({
        control: parentControl,
        name: 'details.desks',
    });
    const linkedModules = useWatch({
        control: parentControl,
        name: 'linkedModules',
    });
    const linkType = useWatch({ control: parentControl, name: 'linkType' });

    const handleSelect = (d: any) => {
        setValue('_demo.date', d);
    };

    const checkinDate = date?.from;
    const checkoutDate = date?.to;

    const [data, setData] = useState<any>();
    const [error, setError] = useState<boolean>(true);

    const getFilteredSpaces = () => {
        if (values.cleaningFeeType !== CleaningFeeType.ByZone) {
            return values?.spaces || [];
        }

        const selectedSpaceIds = values?._demo?.selectedSpaces || [];

        if (selectedSpaceIds.length === 0) {
            return values?.spaces || [];
        }

        return (
            values?.spaces?.filter((space: any) =>
                selectedSpaceIds.includes(space.id),
            ) || []
        );
    };

    useEffect(() => {
        if (checkinDate?.getTime() === checkoutDate?.getTime()) {
            setValue('guests', 0);
        } else {
            if (values.guests === 0) {
                setValue('guests', 1);
            }
        }
    }, [checkinDate, checkoutDate]);

    const updatedValues =
        values.cleaningFeeType === CleaningFeeType.PerRoom
            ? {
                  ...values,
                  basePrice: Number(values.basePrice),
                  unitPrice: Number(values.unitPrice),
              }
            : values.cleaningFeeType === CleaningFeeType.Tiered
              ? {
                    ...values,
                    basePrice: Number(values.basePrice),
                    tiers: values.tiers.map((tier: any) => ({
                        rooms: Number(tier.rooms),
                        price: Number(tier.price),
                    })),
                }
              : values.cleaningFeeType === CleaningFeeType.ByZone
                ? {
                      ...values,
                      basePrice: Number(values.basePrice),
                      spaces: getFilteredSpaces().map((space: any) => ({
                          ...space,
                          cleaningFee: Number(space.cleaningFee),
                      })),
                  }
                : values;

    useEffect(() => {
        const linkedModuleIds = (linkedModules ?? []).map(
            (module: { id: number }) => module.id,
        );
        const combinedModules =
            allAssets?.filter((asset: any) =>
                linkedModuleIds.includes(asset.id),
            ) ?? [];

        const linkedModulesBedrooms = sum(
            combinedModules.map(
                (module: FormValues) =>
                    (module.details as PropertyDetails).bedrooms,
            ),
        );
        const fetchData = async () => {
            try {
                if (!date) return;

                const request = {
                    charges: [updatedValues],
                    startDate: format(checkinDate, 'yyyy-MM-dd'),
                    endDate: format(checkoutDate, 'yyyy-MM-dd'),
                    checkinTime: '08:00',
                    checkoutTime: '17:00',
                    module: {
                        bedRooms: bedrooms
                            ? bedrooms +
                              (linkType === 'combine'
                                  ? linkedModulesBedrooms
                                  : 0)
                            : 1,
                        privateOffices: privateOffices ?? 1,
                        meetingRooms: meetingRooms ?? 1,
                        spaces: getFilteredSpaces().map((space: any) => ({
                            ...space,
                            cleaningFee: Number(space.cleaningFee),
                        })),
                    },
                    overnightGuests: values?.guests,
                    spaces: values?._demo?.selectedSpaces
                        ? values?._demo?.selectedSpaces.map((space: any) => ({
                              id: space,
                              startDate: format(checkinDate, 'yyyy-MM-dd'),
                              endDate: format(checkoutDate, 'yyyy-MM-dd'),
                              attendees: [],
                          }))
                        : [],
                    entireVenue: values?.spaceType === 'entireVenue',
                    privateOffices:
                        values?.spaceType === 'privateOffice' ? 1 : 0,
                    meetingRooms: values?.spaceType === 'meetingRoom' ? 1 : 0,
                    minHireHours: minHireHours ?? 24,
                    type: experience?.type,
                };

                const response = await calculatePrice([
                    loggedInUser?.company,
                    request,
                ]).unwrap();

                setData(response);
                setError(false);
            } catch (_error) {
                setError(true);
            }
        };

        fetchData();
    }, [date, values]);

    let availability = {};
    if (experience?.type === ExperienceType.Commercial) {
        availability = {
            desks,
            privateOffices,
            meetingRooms,
        };
    }

    const filteredSpaces = values?.spaces?.filter((space: any) =>
        values?.spaceType === 'meetingRoom'
            ? space.spaceType === 'boardroom'
            : values?.spaceType === 'privateOffice'
              ? space.spaceType === 'privateOffice'
              : true,
    );

    const availableSpaces = getAvailableSpaces(availability, experience);

    const theme = useTheme();

    return (
        <Paper variant='outlined' sx={{ p: 2 }}>
            <Stack spacing={2}>
                <style>{css}</style>
                <Box>
                    <Typography variant='h6' fontWeight='bold' gutterBottom>
                        Preview
                    </Typography>
                    <Typography variant='body3'>
                        Preview how your rates will look to the guest and adjust
                        to maximize revenue.
                    </Typography>
                </Box>
                <FormCalendar
                    name='_demo.date'
                    control={control}
                    onSelect={handleSelect}
                    hideNavigation
                />

                {values.cleaningFeeType !== CleaningFeeType.ByZone && (
                    <Box display='flex' justifyContent='space-between'>
                        <InputLabel> Overnight Guests</InputLabel>
                        <FormCounter
                            disableDecrease={values.guests === 1}
                            name={'guests'}
                            disabled={
                                checkinDate?.getTime() ===
                                checkoutDate?.getTime()
                            }
                        />
                    </Box>
                )}

                {experience?.type === ExperienceType.Commercial && (
                    <Box pb='20px'>
                        <InputLabel>Workspace Type</InputLabel>
                        <FormAutocomplete
                            name='spaceType'
                            control={control}
                            options={[
                                ...availableSpaces,
                                {
                                    value: 'entireVenue',
                                    label: 'Entire Venue',
                                },
                            ]}
                        />
                    </Box>
                )}

                {values.cleaningFeeType === CleaningFeeType.ByZone &&
                    values.spaces &&
                    values.spaces.length > 0 &&
                    values.spaceType !== 'desk' && (
                        <Box>
                            <FormCardSelect
                                name='_demo.selectedSpaces'
                                control={control}
                                spaces={filteredSpaces}
                                spaceType={values.spaceType}
                            />
                        </Box>
                    )}

                {error ? (
                    <FormHelperText>
                        Please complete form to see changes reflected
                    </FormHelperText>
                ) : (
                    <Collapse in={!!date}>
                        <Box>
                            <Typography variant='overline' fontWeight='bold'>
                                Price Breakdown
                            </Typography>

                            <TransitionGroup>
                                {data?.charges
                                    ?.filter((charge: any) => charge.total > 0)
                                    .map((charge: any, index: number) => {
                                        return (
                                            <Collapse key={index}>
                                                <Grid
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'space-between',
                                                        pb: 0.2,
                                                    }}
                                                >
                                                    <Typography variant='body3'>
                                                        {
                                                            getCleaningFeeText(
                                                                charge,
                                                                availableZones,
                                                            ).description
                                                        }
                                                    </Typography>
                                                    <Typography variant='body3'>
                                                        {
                                                            getCleaningFeeText(
                                                                charge,
                                                                availableZones,
                                                            ).amount
                                                        }
                                                    </Typography>
                                                </Grid>
                                            </Collapse>
                                        );
                                    })}
                            </TransitionGroup>

                            <Grid
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    pt: 0.2,
                                    pb: 0.2,
                                    borderTop: `1px solid ${theme.palette.faded.main}`,
                                }}
                            >
                                <Typography variant='body3'>10% GST</Typography>
                                <Typography variant='body3'>
                                    {formatCurrency(data?.bookingTotal?.gst)}
                                </Typography>
                            </Grid>
                            <Grid
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography fontWeight='bold' variant='body3'>
                                    Total
                                </Typography>
                                <Typography fontWeight='bold' variant='body3'>
                                    {formatCurrency(data?.bookingTotal?.total)}
                                </Typography>
                            </Grid>
                        </Box>
                    </Collapse>
                )}
            </Stack>
        </Paper>
    );
};

export default CalendarView;
